import { useState, useCallback, useEffect } from 'react';
import {
  ExternalLink,
  RefreshCw,
  Maximize2,
  Smartphone,
  Tablet,
  Monitor,
  Link as LinkIcon,
  MessageSquare,
  PanelRightClose,
  PanelRightOpen,
  Code2,
  Eye,
  Save,
  RotateCcw,
  Columns,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import type { Prototype } from '@/types';
import { MockChatPanel } from '@/components/prototype/MockChatPanel';
import { CodeEditor } from '@/components/prototype/CodeEditor';
import { PreviewRenderer } from '@/components/prototype/PreviewRenderer';
import { REACT_TEMPLATE } from '@/components/prototype/TranspilerService';

interface PrototypeViewerProps {
  prototype: Prototype | null;
  projectId?: string;
  projectName?: string;
  onSave?: (code: string) => Promise<void>;
}

type DeviceSize = 'mobile' | 'tablet' | 'desktop' | 'full';
type ViewMode = 'url' | 'code';
type LayoutMode = 'split' | 'preview' | 'editor';

const deviceSizes: Record<DeviceSize, { width: string; label: string; icon: typeof Smartphone }> = {
  mobile: { width: '375px', label: '모바일', icon: Smartphone },
  tablet: { width: '768px', label: '태블릿', icon: Tablet },
  desktop: { width: '1280px', label: '데스크탑', icon: Monitor },
  full: { width: '100%', label: '전체', icon: Maximize2 },
};

export function PrototypeViewer({ prototype, projectId, projectName, onSave }: PrototypeViewerProps) {
  // 기본 상태
  const [url, setUrl] = useState(prototype?.url || '');
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('full');
  const [key, setKey] = useState(0);
  const [showMockChat, setShowMockChat] = useState(false);

  // 코드 편집 상태
  const [viewMode, setViewMode] = useState<ViewMode>(prototype?.content ? 'code' : 'url');
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('split');
  const [code, setCode] = useState(prototype?.content || REACT_TEMPLATE);
  const [originalCode, setOriginalCode] = useState(prototype?.content || REACT_TEMPLATE);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [errorInfo, setErrorInfo] = useState<{ line?: number; message: string } | null>(null);

  // 프로토타입 변경 시 상태 업데이트
  useEffect(() => {
    if (prototype?.content) {
      setCode(prototype.content);
      setOriginalCode(prototype.content);
      setViewMode('code');
    }
    if (prototype?.url) {
      setUrl(prototype.url);
    }
  }, [prototype]);

  // 코드 변경 감지
  useEffect(() => {
    setHasUnsavedChanges(code !== originalCode);
  }, [code, originalCode]);

  const handleRefresh = () => {
    setKey((prev) => prev + 1);
  };

  const handleOpenExternal = () => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleLoadUrl = () => {
    setKey((prev) => prev + 1);
  };

  const handleCodeChange = useCallback((newCode: string) => {
    setCode(newCode);
  }, []);

  const handleSave = async () => {
    if (!onSave) return;

    setIsSaving(true);
    try {
      await onSave(code);
      setOriginalCode(code);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('저장 실패:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setCode(originalCode);
    setHasUnsavedChanges(false);
  };

  const handleError = useCallback((error: { line?: number; message: string } | null) => {
    setErrorInfo(error);
  }, []);

  // 빈 상태 (프로토타입 없음)
  if (!prototype && !url && viewMode === 'url') {
    return (
      <div className="flex h-full flex-col">
        {/* 모드 선택 탭 */}
        <div className="border-b px-4 py-2">
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList>
              <TabsTrigger value="url" className="gap-2">
                <LinkIcon className="h-4 w-4" />
                URL
              </TabsTrigger>
              <TabsTrigger value="code" className="gap-2">
                <Code2 className="h-4 w-4" />
                코드 편집
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="flex flex-1 flex-col items-center justify-center p-8">
          <div className="rounded-full bg-muted p-4">
            <LinkIcon className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="mt-4 text-lg font-semibold">아직 프로토타입이 없습니다</h3>
          <p className="mt-2 max-w-sm text-center text-sm text-muted-foreground">
            URL을 입력하거나 &quot;코드 편집&quot; 탭에서 직접 React 코드를 작성하세요.
          </p>

          <div className="mt-6 flex w-full max-w-md gap-2">
            <Input
              placeholder="프로토타입 URL 입력..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLoadUrl()}
            />
            <Button onClick={handleLoadUrl} disabled={!url}>
              불러오기
            </Button>
          </div>

          <div className="mt-8 grid gap-4 text-center">
            <p className="text-sm text-muted-foreground">지원 플랫폼:</p>
            <div className="flex gap-4">
              <div className="rounded-lg border px-4 py-2 text-sm">CodeSandbox</div>
              <div className="rounded-lg border px-4 py-2 text-sm">StackBlitz</div>
              <div className="rounded-lg border px-4 py-2 text-sm">Vercel</div>
              <div className="rounded-lg border px-4 py-2 text-sm">사용자 정의 URL</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-4">
          {/* 모드 선택 */}
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList className="h-8">
              <TabsTrigger value="url" className="h-7 gap-1.5 px-2 text-xs">
                <LinkIcon className="h-3.5 w-3.5" />
                URL
              </TabsTrigger>
              <TabsTrigger value="code" className="h-7 gap-1.5 px-2 text-xs">
                <Code2 className="h-3.5 w-3.5" />
                코드
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* URL 모드: URL 입력 */}
          {viewMode === 'url' && (
            <>
              <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="URL 입력..."
                className="w-80"
                onKeyDown={(e) => e.key === 'Enter' && handleLoadUrl()}
              />
              <Button variant="outline" size="icon" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </>
          )}

          {/* 코드 모드: 레이아웃 및 저장 */}
          {viewMode === 'code' && (
            <>
              <div className="flex items-center rounded-lg border p-1">
                <Button
                  variant={layoutMode === 'editor' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setLayoutMode('editor')}
                  title="에디터만"
                >
                  <Code2 className="h-4 w-4" />
                </Button>
                <Button
                  variant={layoutMode === 'split' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setLayoutMode('split')}
                  title="분할 보기"
                >
                  <Columns className="h-4 w-4" />
                </Button>
                <Button
                  variant={layoutMode === 'preview' ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setLayoutMode('preview')}
                  title="미리보기만"
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </div>

              <div className="h-6 w-px bg-border" />

              <Button
                variant="outline"
                size="sm"
                onClick={handleReset}
                disabled={!hasUnsavedChanges}
                className="gap-1.5"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                초기화
              </Button>

              {onSave && (
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!hasUnsavedChanges || isSaving}
                  className="gap-1.5"
                >
                  <Save className="h-3.5 w-3.5" />
                  {isSaving ? '저장 중...' : '저장'}
                </Button>
              )}

              {hasUnsavedChanges && (
                <span className="text-xs text-amber-600">저장되지 않은 변경사항</span>
              )}
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* 디바이스 크기 선택 */}
          <Select
            value={deviceSize}
            onValueChange={(v) => setDeviceSize(v as DeviceSize)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(deviceSizes).map(([key, value]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <value.icon className="h-4 w-4" />
                    {value.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {viewMode === 'url' && (
            <Button variant="outline" size="icon" onClick={handleOpenExternal}>
              <ExternalLink className="h-4 w-4" />
            </Button>
          )}

          <div className="h-6 w-px bg-border" />

          <Button
            variant={showMockChat ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => setShowMockChat(!showMockChat)}
            className="gap-2"
          >
            <MessageSquare className="h-4 w-4" />
            모의 채팅
            {showMockChat ? (
              <PanelRightClose className="h-4 w-4" />
            ) : (
              <PanelRightOpen className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* URL 모드 */}
        {viewMode === 'url' && (
          <div className="flex flex-1 items-center justify-center overflow-auto bg-muted/30 p-4">
            <div
              className={cn(
                'h-full bg-background shadow-lg transition-all duration-300',
                deviceSize !== 'full' && 'rounded-lg border'
              )}
              style={{
                width: deviceSizes[deviceSize].width,
                maxWidth: '100%',
              }}
            >
              {url ? (
                <iframe
                  key={key}
                  src={url}
                  className="h-full w-full rounded-lg"
                  title="프로토타입 미리보기"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <p className="text-muted-foreground">미리 보려면 URL을 입력하세요</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 코드 모드 */}
        {viewMode === 'code' && (
          <div className="flex flex-1 overflow-hidden">
            {/* 에디터 패널 */}
            {(layoutMode === 'editor' || layoutMode === 'split') && (
              <div
                className={cn(
                  'flex flex-col overflow-hidden border-r',
                  layoutMode === 'split' ? 'w-1/2' : 'flex-1'
                )}
              >
                <CodeEditor
                  value={code}
                  onChange={handleCodeChange}
                  language="typescript"
                  theme="vs-dark"
                  errorLine={errorInfo?.line}
                  errorMessage={errorInfo?.message}
                  className="flex-1"
                />
              </div>
            )}

            {/* 미리보기 패널 */}
            {(layoutMode === 'preview' || layoutMode === 'split') && (
              <div
                className={cn(
                  'flex flex-col overflow-hidden bg-muted/30',
                  layoutMode === 'split' ? 'w-1/2' : 'flex-1'
                )}
              >
                <div className="flex flex-1 items-center justify-center p-4">
                  <div
                    className={cn(
                      'h-full bg-background shadow-lg transition-all duration-300 overflow-hidden',
                      deviceSize !== 'full' && 'rounded-lg border'
                    )}
                    style={{
                      width: deviceSizes[deviceSize].width,
                      maxWidth: '100%',
                    }}
                  >
                    <PreviewRenderer
                      code={code}
                      language="tsx"
                      theme="light"
                      onError={handleError}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Mock Chat Panel */}
        {showMockChat && (
          <div className="w-96 border-l shadow-lg">
            <MockChatPanel projectId={projectId} projectName={projectName || '프로토타입'} />
          </div>
        )}
      </div>

      {/* Device Indicator */}
      {deviceSize !== 'full' && (
        <div className="border-t bg-muted/30 px-4 py-2 text-center text-xs text-muted-foreground">
          {deviceSizes[deviceSize].label} 미리보기 ({deviceSizes[deviceSize].width})
        </div>
      )}
    </div>
  );
}
